/**
 * Server-side counterpart to `site-config.tsx`.
 *
 * The client provider in `site-config.tsx` is `'use client'` and only
 * fetches config on mount in the browser. Server code (security.txt route,
 * RootLayout's `generateMetadata` + JSON-LD, etc.) cannot use that hook,
 * so this helper does its own fetch with Next.js's built-in fetch cache.
 *
 * Caching: `next: { revalidate: 3600 }` means the same response is shared
 * across all server-component reads in the same and subsequent requests for
 * up to one hour. Admins editing the config in the panel will see a stale
 * security.txt / sameAs for at most that long. Bump `revalidate` down if
 * you need faster propagation.
 *
 * Failure mode: if the backend is unreachable we return defaults — the same
 * behavior the client provider has. Better to render a slightly-wrong page
 * than to crash the request.
 */
import { DEFAULT_SITE_CONFIG, type SiteConfig } from '@/lib/site-config';
import { getApiBaseUrl } from '@/lib/api-url';

const BASE_URL = getApiBaseUrl();

export async function getServerSiteConfig(): Promise<SiteConfig> {
  try {
    const res = await fetch(`${BASE_URL}/public/config`, {
      next: { revalidate: 3600 },
    });
    if (!res.ok) return DEFAULT_SITE_CONFIG;
    const data = (await res.json()) as Partial<SiteConfig>;
    return { ...DEFAULT_SITE_CONFIG, ...data };
  } catch {
    return DEFAULT_SITE_CONFIG;
  }
}

/**
 * Only http:// and https:// schemes survive. Anything else — `javascript:`,
 * `data:`, `file:`, malformed strings — gets filtered. The threat model is a
 * compromised admin account injecting `javascript:fetch(...)` into a social
 * URL field; `<a href={url}>` would then execute that on click, in the site's
 * origin, with access to user cookies.
 *
 * Exported so the client (Footer) can apply the same validation as the
 * server (JSON-LD `sameAs` builder). Keeping one source of truth.
 */
export function isSafeHttpUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  const trimmed = url.trim();
  if (trimmed.length === 0) return false;
  // No CR/LF/tab — header injection / display poisoning.
  if (/[\r\n\t]/.test(trimmed)) return false;
  return /^https?:\/\//i.test(trimmed);
}

/**
 * Pull every safe social URL out of the config so callers can iterate
 * without re-listing the keys. Order is meaningful — used in the Footer
 * to render icons left-to-right and in the JSON-LD `sameAs` array.
 *
 * URLs that aren't valid http(s) are dropped — see `isSafeHttpUrl`.
 */
export function socialUrlsFromConfig(cfg: SiteConfig): { key: string; url: string; label: string }[] {
  const candidates: { key: string; url: string; label: string }[] = [
    { key: 'twitter',   url: cfg['social.twitter_url'],   label: 'Twitter / X' },
    { key: 'github',    url: cfg['social.github_url'],    label: 'GitHub' },
    { key: 'linkedin',  url: cfg['social.linkedin_url'],  label: 'LinkedIn' },
    { key: 'instagram', url: cfg['social.instagram_url'], label: 'Instagram' },
    { key: 'youtube',   url: cfg['social.youtube_url'],   label: 'YouTube' },
  ];
  return candidates
    .map((s) => ({ ...s, url: s.url?.trim() ?? '' }))
    .filter((s) => isSafeHttpUrl(s.url));
}
